import * as THREE from 'three';
import { ItemId } from '../items/ItemRegistry.js';
import { BlockId } from '../world/BlockType.js';

/**
 * BowSystem - Bow and arrow combat for the player
 *
 * - Right-click with bow selected: start charging
 * - Release right-click: fire arrow projectile
 * - Arrow damages mobs on hit (damage = 2 + charge * 8, max 10)
 * - Arrows stick in terrain on impact (removed after 30s)
 * - Consumes 1 arrow from inventory per shot
 */

const MAX_CHARGE_TIME = 1.5; // seconds to reach full charge
const ARROW_SPEED = 30;       // blocks per second
const ARROW_GRAVITY = 12;     // gravity acceleration
const ARROW_LIFETIME = 30;    // seconds before stuck arrows despawn
const ARROW_HIT_RADIUS = 0.8; // hit detection radius for mobs
const PX = 1 / 16;

function simpleBox(w, h, d, color) {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
}

/**
 * Create a 3D arrow mesh (same style as skeleton arrows).
 */
function createArrowMesh() {
  const S = PX * 1.8;
  const group = new THREE.Group();

  // Arrow shaft
  const shaft = simpleBox(0.3 * S, 0.3 * S, 8 * S, 0x5A3A1A);
  group.add(shaft);

  // Arrow head (darker, pointy)
  const head = simpleBox(0.8 * S, 0.8 * S, 2 * S, 0x555555);
  head.position.set(0, 0, 5 * S);
  group.add(head);

  // Fletching (feather at the back)
  const fletchL = simpleBox(1.5 * S, 0.2 * S, 2 * S, 0xEEEEEE);
  fletchL.position.set(0, 0.5 * S, -3.5 * S);
  group.add(fletchL);
  const fletchR = simpleBox(0.2 * S, 1.5 * S, 2 * S, 0xEEEEEE);
  fletchR.position.set(0, 0, -3.5 * S);
  group.add(fletchR);

  return group;
}

export class BowSystem {
  /**
   * @param {THREE.Scene} scene
   * @param {THREE.Camera} camera
   * @param {object} inventory - Player Inventory instance
   */
  constructor(scene, camera, inventory) {
    this.scene = scene;
    this.camera = camera;
    this.inventory = inventory;

    // Charging state
    this.isCharging = false;
    this.chargeTime = 0;

    // Active player arrows in flight
    this.arrows = [];

    // Stuck arrows (embedded in terrain)
    this.stuckArrows = [];

    // Charge indicator UI element
    this.chargeBar = null;
    this._createChargeUI();
  }

  /**
   * Create the charge indicator bar (below crosshair).
   */
  _createChargeUI() {
    this.chargeBar = document.createElement('div');
    this.chargeBar.id = 'bow-charge-bar';
    this.chargeBar.style.cssText = `
      position: fixed;
      left: 50%;
      top: calc(50% + 20px);
      transform: translateX(-50%);
      width: 40px;
      height: 4px;
      background: rgba(0,0,0,0.5);
      border: 1px solid rgba(255,255,255,0.3);
      z-index: 90;
      display: none;
      pointer-events: none;
    `;

    this.chargeFill = document.createElement('div');
    this.chargeFill.style.cssText = `
      width: 0%;
      height: 100%;
      background: #FF8800;
      transition: background 0.1s;
    `;
    this.chargeBar.appendChild(this.chargeFill);
    document.body.appendChild(this.chargeBar);
  }

  /**
   * Check if the player currently has a bow selected in the hotbar.
   */
  hasBowSelected() {
    const slot = this.inventory.getSelectedSlot();
    return slot && slot.itemId === ItemId.BOW;
  }

  /**
   * Check if the player has arrows in their inventory.
   */
  hasArrows() {
    // Check hotbar
    for (const slot of this.inventory.slots) {
      if (slot && slot.itemId === ItemId.ARROW_ITEM && slot.count > 0) return true;
    }
    // Check storage
    for (const slot of this.inventory.storage) {
      if (slot && slot.itemId === ItemId.ARROW_ITEM && slot.count > 0) return true;
    }
    return false;
  }

  /**
   * Consume one arrow from inventory. Returns true if successful.
   */
  consumeArrow() {
    // Check hotbar first
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const slot = this.inventory.slots[i];
      if (slot && slot.itemId === ItemId.ARROW_ITEM && slot.count > 0) {
        slot.count--;
        if (slot.count <= 0) this.inventory.slots[i] = null;
        return true;
      }
    }
    // Check storage
    for (let i = 0; i < this.inventory.storage.length; i++) {
      const slot = this.inventory.storage[i];
      if (slot && slot.itemId === ItemId.ARROW_ITEM && slot.count > 0) {
        slot.count--;
        if (slot.count <= 0) this.inventory.storage[i] = null;
        return true;
      }
    }
    return false;
  }

  /**
   * Start charging the bow. Called on right-click down.
   */
  startCharge() {
    if (!this.hasBowSelected()) return;
    if (!this.hasArrows()) return;

    this.isCharging = true;
    this.chargeTime = 0;
    this.chargeBar.style.display = 'block';
  }

  /**
   * Get the current charge level (0 to 1).
   */
  getChargeLevel() {
    if (!this.isCharging) return 0;
    return Math.min(this.chargeTime / MAX_CHARGE_TIME, 1);
  }

  /**
   * Release the bow charge and fire an arrow.
   * @param {THREE.Vector3} eyePos - Player eye position
   * @param {THREE.Vector3} lookDir - Normalized camera look direction
   */
  releaseCharge(eyePos, lookDir) {
    if (!this.isCharging) return;

    const charge = this.getChargeLevel();
    this.isCharging = false;
    this.chargeBar.style.display = 'none';

    // Minimum charge threshold to fire
    if (charge < 0.1) return;

    // Consume an arrow from inventory
    if (!this.consumeArrow()) return;

    // Calculate arrow velocity
    const speed = ARROW_SPEED * (0.3 + charge * 0.7); // Min 30% speed at low charge
    const velocity = lookDir.clone().normalize().multiplyScalar(speed);

    // Create arrow mesh
    const mesh = createArrowMesh();
    const startPos = eyePos.clone().add(lookDir.clone().multiplyScalar(0.5));
    mesh.position.copy(startPos);

    // Orient arrow along direction
    const target = startPos.clone().add(lookDir);
    mesh.lookAt(target);

    this.scene.add(mesh);

    const arrow = {
      mesh,
      position: startPos.clone(),
      velocity,
      damage: 2 + charge * 8, // 2 at min charge, 10 at full
      lifetime: 5.0,          // 5 seconds in flight before despawning
      charge,
      stuck: false,
    };

    this.arrows.push(arrow);
  }

  /**
   * Cancel charging (e.g., when switching items or opening UI).
   */
  cancelCharge() {
    this.isCharging = false;
    this.chargeBar.style.display = 'none';
  }

  /**
   * Update the bow system each frame.
   * @param {number} dt - Delta time in seconds
   * @param {object} world - World object for block collision
   * @returns {Array} Array of hit results: [{ entity, damage }]
   */
  update(dt, world) {
    // Update charge
    if (this.isCharging) {
      // Cancel if bow no longer selected or out of arrows
      if (!this.hasBowSelected() || !this.hasArrows()) {
        this.cancelCharge();
      } else {
        this.chargeTime += dt;
        const level = this.getChargeLevel();
        this.chargeFill.style.width = (level * 100) + '%';
        // Color changes: orange -> yellow -> green at full charge
        if (level >= 1) {
          this.chargeFill.style.background = '#44FF44';
        } else if (level >= 0.5) {
          this.chargeFill.style.background = '#FFCC00';
        } else {
          this.chargeFill.style.background = '#FF8800';
        }
      }
    }

    // Update flying arrows
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const arrow = this.arrows[i];

      // Apply gravity
      arrow.velocity.y -= ARROW_GRAVITY * dt;

      // Move arrow
      arrow.position.x += arrow.velocity.x * dt;
      arrow.position.y += arrow.velocity.y * dt;
      arrow.position.z += arrow.velocity.z * dt;

      // Update mesh
      if (arrow.mesh) {
        arrow.mesh.position.copy(arrow.position);
        // Orient along velocity
        if (arrow.velocity.lengthSq() > 0.1) {
          const target = arrow.position.clone().add(arrow.velocity.clone().normalize());
          arrow.mesh.lookAt(target);
        }
      }

      // Decrement lifetime
      arrow.lifetime -= dt;
      if (arrow.lifetime <= 0) {
        this.scene.remove(arrow.mesh);
        this.arrows.splice(i, 1);
        continue;
      }

      // Check block collision
      if (world) {
        const bx = Math.floor(arrow.position.x);
        const by = Math.floor(arrow.position.y);
        const bz = Math.floor(arrow.position.z);
        const block = world.getBlock(bx, by, bz);
        if (block !== undefined && block !== BlockId.AIR && block !== BlockId.WATER) {
          // Arrow stuck in terrain
          this._stickArrow(arrow);
          this.arrows.splice(i, 1);
          continue;
        }
      }

      // Below world = remove
      if (arrow.position.y < -10) {
        this.scene.remove(arrow.mesh);
        this.arrows.splice(i, 1);
        continue;
      }
    }

    // Update stuck arrows (despawn after ARROW_LIFETIME)
    for (let i = this.stuckArrows.length - 1; i >= 0; i--) {
      const stuck = this.stuckArrows[i];
      stuck.age += dt;
      if (stuck.age >= ARROW_LIFETIME) {
        this.scene.remove(stuck.mesh);
        this.stuckArrows.splice(i, 1);
      }
    }
  }

  /**
   * Check all flying arrows against a list of entities for hits.
   * Returns array of { arrowIndex, entity, damage } for hits.
   * The caller is responsible for applying damage and removing entities.
   *
   * @param {Array} entities - Array of objects with { position, health, type, ... }
   * @returns {Array<{ entity: object, damage: number }>}
   */
  checkHits(entities) {
    const hits = [];

    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const arrow = this.arrows[i];

      for (const entity of entities) {
        if (!entity.position || entity.health === undefined || entity.health <= 0) continue;

        // Distance check (center of entity is ~0.9 blocks above feet)
        const ex = entity.position.x;
        const ey = entity.position.y + 0.9;
        const ez = entity.position.z;

        const dx = arrow.position.x - ex;
        const dy = arrow.position.y - ey;
        const dz = arrow.position.z - ez;
        const distSq = dx * dx + dy * dy + dz * dz;

        if (distSq < ARROW_HIT_RADIUS * ARROW_HIT_RADIUS) {
          hits.push({
            entity,
            damage: arrow.damage,
          });

          // Stick the arrow (visual only)
          this._stickArrow(arrow);
          this.arrows.splice(i, 1);
          break; // This arrow is consumed
        }
      }
    }

    return hits;
  }

  /**
   * Convert a flying arrow into a stuck arrow (stays in world briefly).
   */
  _stickArrow(arrow) {
    // Stop the arrow in place
    arrow.velocity.set(0, 0, 0);
    this.stuckArrows.push({
      mesh: arrow.mesh,
      age: 0,
    });
  }

  /**
   * Clean up all arrows (e.g., on death/respawn).
   */
  clear() {
    for (const arrow of this.arrows) {
      this.scene.remove(arrow.mesh);
    }
    this.arrows.length = 0;

    for (const stuck of this.stuckArrows) {
      this.scene.remove(stuck.mesh);
    }
    this.stuckArrows.length = 0;

    this.cancelCharge();
  }
}
